<template>
  <v-container fluid>
    <v-card v-if="record" variant="outlined">
      <v-card-title class="d-flex flex-wrap align-center ga-2 bg-blue-grey-lighten-4">
        <span>Finished-Goods Delivery {{ record.code }}</span>
        <v-spacer />
        <v-chip :color="approvalColor(record.approval_status)" size="small">{{ record.approval_status || (record.status ? 'Pending' : 'Draft') }}</v-chip>
        <v-btn class="d-print-none" icon="mdi-printer" variant="text" @click="printPage" />
      </v-card-title>
      <v-card-text class="pt-4">
        <v-row dense>
          <v-col cols="6" md="3"><Info label="Production" :value="record.production?.code || '-'" /></v-col>
          <v-col cols="6" md="3"><Info label="Delivery Date" :value="dateOnly(record.delivery_date)" /></v-col>
          <v-col cols="6" md="3"><Info label="Destination Warehouse" :value="record.warehouse?.name || '-'" /></v-col>
          <v-col cols="6" md="3"><Info label="Workflow" :value="record.production?.workflow_status?.replaceAll('_', ' ') || '-'" /></v-col>
          <v-col cols="12"><Info label="Remarks" :value="record.remarks || '-'" /></v-col>
        </v-row>

        <v-table density="compact" class="border-sm mt-4">
          <thead>
            <tr>
              <th>Finished Product</th>
              <th>Batch</th>
              <th>Manufactured</th>
              <th>Expiry</th>
              <th class="text-end">Delivery Qty</th>
              <th class="text-end">Inventory Received</th>
              <th class="text-end">Material/Packaging Unit Cost</th>
              <th class="text-end">Labour/Overhead Unit Cost</th>
              <th class="text-end">Full Unit Cost</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="detail in record.details || []" :key="detail.id">
              <td>{{ detail.product?.code }} - {{ detail.product?.name }}</td>
              <td>{{ detail.batch_no || detail.production_detail?.batch_no || '-' }}</td>
              <td>{{ dateOnly(detail.manufacturing_date || detail.production_detail?.manufacturing_date) || '-' }}</td>
              <td>{{ dateOnly(detail.expiry_date || detail.production_detail?.expiry_date) || '-' }}</td>
              <td class="text-end">{{ number(detail.quantity) }}</td>
              <td class="text-end">{{ number(detail.received_quantity) }}</td>
              <td class="text-end">{{ number(Number(detail.material_unit_cost || 0) + Number(detail.packaging_unit_cost || 0), 6) }}</td>
              <td class="text-end">{{ number(Number(detail.labour_unit_cost || 0) + Number(detail.overhead_unit_cost || 0), 6) }}</td>
              <td class="text-end font-weight-bold">{{ number(detail.rate, 6) }}</td>
            </tr>
          </tbody>
        </v-table>
      </v-card-text>
    </v-card>
    <v-skeleton-loader v-else type="article, table" />
  </v-container>
</template>

<script setup>
import { defineComponent, h, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";

const Info = defineComponent({
  props: { label: String, value: [String, Number] },
  setup(props) {
    return () => h("div", { class: "info-box" }, [
      h("div", { class: "info-label" }, props.label),
      h("div", { class: "font-weight-medium text-capitalize" }, props.value ?? "-"),
    ]);
  },
});

const route = useRoute();
const store = useStore();
const record = ref(null);

function number(value, digits = 4) {
  return Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: digits });
}

function dateOnly(value) {
  return value ? String(value).slice(0, 10) : "";
}

function approvalColor(status) {
  if (status === "Approved") return "success";
  if (status === "Cancelled" || status === "Rejected") return "error";
  return "warning";
}

function printPage() {
  window.print();
}

onMounted(async () => {
  try {
    const response = await store.dispatch("getPrdDeliveryById", route.params.id);
    record.value = response?.data || response;
  } catch (error) {
    toast.error(error?.response?.data?.message || "Finished-goods delivery details could not be loaded.");
  }
});
</script>

<style scoped>
.info-box { border: 1px solid rgba(0, 0, 0, 0.12); border-radius: 4px; height: 100%; padding: 8px 10px; }
.info-label { color: rgba(0, 0, 0, 0.6); font-size: 0.7rem; text-transform: uppercase; }
</style>
